import {Button} from '#/app/components/Button.jsx';
import {Container} from '#/app/components/Container.jsx';
import {Highlight} from '#/app/components/Highlight.jsx';
import {Image} from '#/app/components/Image.jsx';
import {TrustedBy} from '#/app/components/TrustedBy.jsx';
import {COMPARISON} from '#/app/content/alternatives/comparison.js';

export type CompetitionContent = {
  company: string;
  comparisonIndex: number;
  reasons: {
    title: string;
    content: string[];
  }[];
  highlights: {
    title: string;
    subtitle: string;
    description: React.ReactNode[];
    image: () => JSX.Element;
  }[];
  quote: {
    quote: string;
    person: string;
    title: string;
    image: string;
  };
};

export default function Competition({company, comparisonIndex, highlights, quote, reasons}: CompetitionContent) {
  return (
    <>
      <Container>
        <div className="flex flex-col items-center space-y-12 py-16">
          <h4 className="text-center font-semibold leading-none" style={{fontSize: 80}}>
            Meet the #1
            {' '}
            {company}
            <br />
            alternative
          </h4>
          <p className="mx-auto w-6/12 text-center text-3xl text-gray-700">
            Compare features and decide which route planning platform is best for your business.
          </p>
          <Button
            appearance="primary"
            className="_cta"
            color="sky"
            href="/register"
            size="lg"
          >
            Start Your Free Trial
          </Button>
          <div className="mx-auto w-9/12">
            <Image alt="ufleet dashboard and ufleet driver app screenshots" src="/features-control-center-3.png" />
          </div>
        </div>
      </Container>
      <TrustedBy />
      <Container className="pb-8">
        <div className="py-16">
          <div className="mb-4 text-center text-lg font-semibold uppercase text-sky-500">Features Comparison</div>
          <h2 className="text-center font-semibold">{company} vs Ufleet</h2>
          <div className="mx-auto my-16 w-full rounded-xl border-4 p-4 text-xl text-gray-800 md:w-9/12">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="rounded-lg p-4 text-left font-semibold"></th>
                  <th className="whitespace-nowrap rounded-lg p-4 text-center text-2xl font-semibold text-sky-500">⬢ ufleet</th>
                  <th className="rounded-lg p-4 text-center font-semibold">{company}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((c, i) => {
                  const isCategory = !Array.isArray(c);
                  const tdClassName = 'p-4 rounded-lg';
                  return isCategory ? (
                    <tr className={`${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`} key={i}>
                      <td className={`${tdClassName} font-semibold`} colSpan={3}>
                        {c}
                      </td>
                    </tr>
                  ) : (
                    <tr className={`${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`} key={i}>
                      <td className={`${tdClassName}`}>{c[0]}</td>
                      <td className={`${tdClassName} text-center`}>✅</td>
                      <td className={`${tdClassName} text-center`}>{c[comparisonIndex] ? '✅' : '❌'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="space-y-8 text-center">
            <Button
              appearance="primary"
              className="_cta"
              color="sky"
              href="/register"
              size="lg"
            >
              Try Ufleet for Free
            </Button>
          </div>
        </div>
        <div className="py-16">
          <h2 className="mx-auto w-2/3 text-center font-semibold leading-tight">Why you’ll instantly do better</h2>
          <div className="flex flex-col space-x-0 space-y-12 py-24 text-gray-800 sm:flex-row sm:space-x-16 sm:space-y-0">
            {reasons.map(({content, title}) => (
              <div className="w-full text-center sm:w-1/3" key={title}>
                <h4 className="mb-8 flex h-16 items-end justify-center font-semibold">{title}</h4>
                <ul className="list-inside space-y-2 text-xl [&>li]:before:content-['__']" style={{listStyleType: '"✓"'}}>
                  {content.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex justify-center pb-8">
            <Button
              appearance="primary"
              className="_cta"
              color="amber"
              href="/ufleet-alternatives"
              size="lg"
            >
              Compare Ufleet with Other Competitors
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center pb-24 pt-8">
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="aspect-video w-full rounded-xl border-8 md:w-2/3"
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
              src="https://www.youtube.com/embed/l6wFay1JMAw?si=r7AZ-i2AA0bDav_2"
              title="YouTube video player"
            ></iframe>
          </div>
        </div>
        <div>
          <h2 className="mx-auto mb-16 w-2/3 text-center font-semibold leading-tight">What you get with Ufleet</h2>
          <div className="space-y-16">
            {highlights.map((highlight, i) => (
              <Highlight
                feature={{color: 'sky'}}
                highlight={highlight}
                i={i}
                key={highlight.title}
              />
            ))}
          </div>
        </div>
        <div className="py-16 text-center">
          <Button
            appearance="primary"
            className="_cta"
            color="sky"
            href="/register"
            size="lg"
          >
            Try Ufleet for Free
          </Button>
        </div>

        <div className="space-y-16 py-16">
          <h2 className="mx-auto w-2/3 text-center font-semibold leading-tight">What customers achieved with Ufleet</h2>
          <div className="mx-auto mb-48 flex w-full flex-col space-y-6 rounded-xl bg-gray-100 p-8 text-gray-800 md:w-10/12 md:flex-row md:space-x-12 md:space-y-0 md:p-16">
            <div className="text-xl leading-normal md:w-9/12 md:text-2xl md:leading-normal">“{quote.quote}”</div>
            <div className="flex w-full flex-row space-x-6 md:w-3/12 md:flex-col md:justify-center md:space-x-0">
              <div className="w-1/4 rounded-xl md:mb-4 md:w-full">
                <Image
                  alt={quote.person}
                  className="rounded-xl"
                  src={quote.image}
                />
              </div>
              <div>
                <div className="text-xl font-medium">{quote.person}</div>
                <div className="">{quote.title}</div>
              </div>
            </div>
          </div>
          <div className="text-center">
            <Button
              appearance="primary"
              className="_cta"
              color="orange"
              href="/case-studies/obi"
              size="lg"
            >
              Read Case Study
            </Button>
          </div>
        </div>
      </Container>
    </>
  );
}
